// Packages
import React, { useState, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import { createColumnHelper } from "@tanstack/react-table";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { Box } from "@mui/material";
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import useZustandStore from "zustandStore";

// Icon
import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

// Components
import { Loading } from 'components/Loading';
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton as SolidRectangleButton } from "components/buttons/SolidRectangleButton";
import TableField from "components/Table";
import DrawerField from "components/Drawer";
import GlobalAlert from "components/Notification";
import LinkModal from 'components/LinkModal';

import CreateNewProfileModal from './Modal/CreateNewProfileModal';

// Modal
import CreateNewWirelessNetworkModal from "./Modal/Create";
import UnSavedConfirmModalContainer from 'components/UnSavedConfirmModalContainer';

// Hook
import useAuthScope from "hooks/useAuthScope";

// Local Utils
import {
  getInitWirelessData,
  getSendRequestParams,
  getCleanUpdatePayload,
} from './helper';

// Local const
import {
  getColumnsData,
  defaultBandOptions,
  securityTypeAssembleList,
  securityTypeConfig,
  defaultAlertInfo,
  defaultIsLoading
} from './const';

const WirelessSSID = ({
  showEditGeneral,
  unSavedBlocker,
  onGoToPage,
}) => {
  const [uuids] = useState(() => ({
    read: uuidv4(),
    update: uuidv4(),
    readRadiusProfile: uuidv4(),
    readWirelessScheduleProfile: uuidv4(),
    readMacFilterProfile: uuidv4(),
    readIpFilterProfile: uuidv4(),
    readCaptivePortalProfile: uuidv4(),
  }));

  const { scopeReady } = useOutletContext();
  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const isUnityPro = serviceStatus?.services.unityPro;

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, isAuthReady } = useAuthScope();
  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } = useDataSourceManager();
  const { isDataChanged, onInitBlockerData, onUpdateBlockerData, isUnsavedBlocked, onBlockerApply, onBlockerDiscard } = unSavedBlocker;
  const setGlobalOnApplyFn = useZustandStore(state => state.setGlobalOnApplyFn);
  const setGlobalOnDiscardFn = useZustandStore(state => state.setGlobalOnDiscardFn);

  const [bandCount, setBandCount] = useState({});
  const [originalWirelessData, setOriginalWirelessData] = useState([]);
  const [wirelessData, setWirelessData] = useState([]);
  const [clientsData, setClientsData] = useState([]);

  const [radiusProfileOptions, setRadiusProfileOptions] = useState([]);
  const [wirelessScheduleOptions, setWirelessScheduleOptions] = useState([]);
  const [macFilterOptions, setMacFilterOptions] = useState([]);
  const [ipFilterOptions, setIpFilterOptions] = useState([]);
  const [captivePortalOptions, setCaptivePortalOptions] = useState([]);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [originalRowData, setOriginalRowData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(structuredClone(defaultIsLoading));
  const [alertInfo, setAlertInfo] = useState(structuredClone(defaultAlertInfo));
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const [createProfileModal, setCreateProfileModal] = useState({
    open: false,
    profileType: '',
    page: '',
  });

  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  const {
    navigateSelectedSsidName,
  } = location.state ?? {};

  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) {
      orgId = orgsId;
    }
  } else {
    orgId = realmId;
  }

  const isValidInputStyles = (isValid) => {
    return {
      width: "62.5%",
      "& .MuiOutlinedInput-root": {
        borderRadius: theme.shape.customBorderRadius[1],
        height: "31px",
        backgroundColor: theme.palette.primary.transparent,

        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: isValid
            ? theme.palette.primary.borderGreyPrimary
            : theme.palette.primary.redPrimary,
        },

        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: isValid
            ? theme.palette.primary.mainHover
            : theme.palette.primary.redPrimary,
        },

        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: isValid
            ? theme.palette.primary.main
            : theme.palette.primary.redPrimary,
        },
      },
      // Hide native number spinners (WebKit)
      '& .MuiOutlinedInput-root input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-root input[type="number"]::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      // Firefox / generic appearance
      '& .MuiOutlinedInput-root input[type="number"]': {
        MozAppearance: 'textfield',
        appearance: 'textfield',
      },
    };
  };

  const onCreateNewProfile = ({ profileType, page }) => {
    setCreateProfileModal({
      open: true,
      profileType,
      page,
    });
  };

  const onCloseCreateProfileModal = () => {
    setCreateProfileModal((prev) => ({ ...prev, open: false }));
  };

  const getWirelessSSID = () => {
    setIsLoading(prev => {
      return {
        ...prev,
        wireless: true,
      }
    })

    const { apiUrl, requestParams } = getSendRequestParams({ auth, method: "read" });

    sendRequest(
      apiUrl,
      uuids.read,
      requestParams,
    );
  };

  useEffect(() => {
    if (!scopeReady || !serviceStatus) return;

    if(isAuthReady) getWirelessSSID();
  }, [isAuthReady]);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.readRadiusProfile)) return;

    const response = getData(uuids.readRadiusProfile);

    if (response && response?.data) {
      const radiusProfileList = response.data
        .filter((item) => item.id && item.name)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }));

      setRadiusProfileOptions(radiusProfileList);
    }

    setIsLoading(prev => {
      return {
        ...prev,
        radius: false,
      }
    });

  }, [dataMap?.[uuids.readRadiusProfile]]);


  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.readWirelessScheduleProfile)) return;

    const response = getData(uuids.readWirelessScheduleProfile);

    if (response && response?.data) {
      const wirelessScheduleList = response.data
        .filter((item) => item.id && item.name && item.profileType === 'wireless')
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }));

      const options = [
        { value: "disabled", label: "Disabled" },
        ...wirelessScheduleList,
      ];

      setWirelessScheduleOptions(options);
    }

    setIsLoading(prev => {
      return {
        ...prev,
        wirelessSchedule: false,
      }
    });
  }, [dataMap?.[uuids.readWirelessScheduleProfile]]);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.readMacFilterProfile)) return;

    const response = getData(uuids.readMacFilterProfile);

    if (response && response?.data) {
      const macFilterList = response.data
        .filter((item) => item.id && item.name)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }));

      const options = [
        { value: "disabled", label: "Disabled" },
        ...macFilterList,
      ];

      setMacFilterOptions(options);
    }

    setIsLoading(prev => {
      return {
        ...prev,
        macFilter: false,
      }
    });
  }, [dataMap?.[uuids.readMacFilterProfile]]);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.readIpFilterProfile)) return;

    const response = getData(uuids.readIpFilterProfile);

    if (response && response?.data) {
      const ipFilterList = response.data
        .filter((item) => item.id && item.name)
        .map(({ id, name }) => ({
          value: id,
          label: name,
        }));

      const options = [
        { value: "disabled", label: "Disabled" },
        ...ipFilterList,
      ];

      setIpFilterOptions(options);
    }

    setIsLoading(prev => {
      return {
        ...prev,
        ipFilter: false,
      }
    });
  }, [dataMap?.[uuids.readIpFilterProfile]]);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.readCaptivePortalProfile)) return;

    const response = getData(uuids.readCaptivePortalProfile);

    if (response && response?.data) {
      const captivePortalsList = response.data
        .filter((item) => item.id && item.name)
        .map(({ id, name, authType }) => ({
          value: id,
          label: name,
          type: authType,
        }));

      const options = [
        { value: "disabled", label: "Disabled" },
        ...captivePortalsList,
      ];

      setCaptivePortalOptions(options);
    }

    setIsLoading(prev => {
      return {
        ...prev,
        captivePortal: false,
      }
    });
  }, [dataMap?.[uuids.readCaptivePortalProfile]]);

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.read)) return;

    const wirelessResponse = getData(uuids.read);

    if (wirelessResponse) {
      const initWirelessData = getInitWirelessData({ wirelessData: wirelessResponse?.data })
      setWirelessData(structuredClone(initWirelessData));
      setOriginalWirelessData(structuredClone(initWirelessData));

      onInitBlockerData(initWirelessData);

      // set loading flags for supporting profile lists
      setIsLoading(prev => ({
        ...prev,
        radius: true,
        wirelessSchedule: true,
        macFilter: true,
        ipFilter: true,
        captivePortal: true,
      }));

      // fetch supporting lists in one batched request to avoid missing responses
      (async () => {
        try {
          const requests = [
            {
              apiKey: hasManageOrg
                ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/radiusProfile.read"
                : "/orgs/:orgId/sites/:siteId/settings/radiusProfile.read",
              requestUuid: uuids.readRadiusProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg
                ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/profileSchedule.read"
                : "/orgs/:orgId/sites/:siteId/settings/profileSchedule.read",
              requestUuid: uuids.readWirelessScheduleProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg
                ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/profileMacAddrs.read"
                : "/orgs/:orgId/sites/:siteId/settings/profileMacAddrs.read",
              requestUuid: uuids.readMacFilterProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg
                ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/profileIpAddrs.read"
                : "/orgs/:orgId/sites/:siteId/settings/profileIpAddrs.read",
              requestUuid: uuids.readIpFilterProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
            {
              apiKey: hasManageOrg
                ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/captivePortals.read"
                : "/orgs/:orgId/sites/:siteId/settings/captivePortals.read",
              requestUuid: uuids.readCaptivePortalProfile,
              restfulPlaceholders: { entId, orgId, siteId },
            },
          ];

          const { getSendBatchData } = await sendBatch(requests);

          const radiusResp = getSendBatchData({ requestUuid: uuids.readRadiusProfile });
          if (radiusResp?.data) {
            const radiusProfileList = radiusResp.data
              .filter(item => item.id && item.name)
              .map(({ id, name }) => ({ value: id, label: name }));
            setRadiusProfileOptions(radiusProfileList);
          }

          const scheduleResp = getSendBatchData({ requestUuid: uuids.readWirelessScheduleProfile });
          if (scheduleResp?.data) {
            const wirelessScheduleList = scheduleResp.data
              .filter(item => item.id && item.name && item.profileType === 'wireless')
              .map(({ id, name }) => ({ value: id, label: name }));
            setWirelessScheduleOptions([{ value: "disabled", label: "Disabled" }, ...wirelessScheduleList]);
          }

          const macResp = getSendBatchData({ requestUuid: uuids.readMacFilterProfile });
          if (macResp?.data) {
            const macFilterList = macResp.data
              .filter(item => item.id && item.name)
              .map(({ id, name }) => ({ value: id, label: name }));
            setMacFilterOptions([{ value: "disabled", label: "Disabled" }, ...macFilterList]);
          }

          const ipResp = getSendBatchData({ requestUuid: uuids.readIpFilterProfile });
          if (ipResp?.data) {
            const ipFilterList = ipResp.data
              .filter(item => item.id && item.name)
              .map(({ id, name }) => ({ value: id, label: name }));
            setIpFilterOptions([{ value: "disabled", label: "Disabled" }, ...ipFilterList]);
          }

          const captivePortalResp = getSendBatchData({ requestUuid: uuids.readCaptivePortalProfile });
          if (captivePortalResp?.data) {
            const captivePortalsList = captivePortalResp.data
              .filter(item => item.id && item.name)
              .map(({ id, name, authType }) => ({ value: id, label: name, type: authType }));
            setCaptivePortalOptions([{ value: "disabled", label: "Disabled" }, ...captivePortalsList]);
          }
        } catch (e) {
          // Nothing to do.
        } finally {
          setIsLoading(prev => ({
            ...prev,
            radius: false,
            wirelessSchedule: false,
            macFilter: false,
            ipFilter: false,
            captivePortal: false,
          }));
        }
      })();

      if (initWirelessData.length > 0) {
        const tmpBandCount = {};
        for (const item of initWirelessData) {
          if (!Array.isArray(item.band)) continue;

          for (const b of item.band) {
            tmpBandCount[b] = (tmpBandCount[b] || 0) + 1;
          }
        }
        setBandCount(tmpBandCount);
      }
    }

    setIsLoading(prev => {
      return {
        ...prev,
        wireless: false,
      }
    });
  }, [dataMap?.[uuids.read]]);

  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
  };

  const handleDrawerClose = () => {
    setOriginalRowData(null);
    setSelectedRowData(null);
    setDrawerOpen(false);
  };

  const columnHelper = createColumnHelper();
  const columnsData = getColumnsData({ columnHelper });

  const table = useTableInstance({
    tableName: "ssid",
    data: wirelessData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  useEffect(() => {
    if (navigateSelectedSsidName) {
      const matchedSSID = wirelessData.find(ssid => ssid.ssid === navigateSelectedSsidName);
      if (matchedSSID) {
        setOriginalRowData(structuredClone(matchedSSID));
        setSelectedRowData(structuredClone(matchedSSID));
        setDrawerOpen(true);

        const {
          navigateSelectedSsidName,
          ...resetStates
        } = location?.state;

        navigate(location.pathname, {
          replace: true,
          state: resetStates
        });
      }
    }
  }, [navigateSelectedSsidName, wirelessData])

  useEffect(() => {
    if (selectedRowData && wirelessData?.length > 0) {
      const tmpBandCount = {};
      for (const item of wirelessData) {
        if (!Array.isArray(item.band)) continue;
        for (const b of item.band) {
          tmpBandCount[b] = (tmpBandCount[b] || 0) + 1;
        }
      }
      setBandCount(tmpBandCount);
    }
  }, [selectedRowData])

  const checkCreateBtnDisabled = () => {
    const requiredBands = [2, 5, 6];
    return requiredBands.every(k => bandCount[k] >= 8);
  };

  const onApply = () => {
    setIsLoading(prev => {
      return {
        ...prev,
        updateWireless: true,
      }
    });

    const { apiUrl, requestParams } = getSendRequestParams({ auth, method: "update" });
    const payload = getCleanUpdatePayload({ wirelessData });

    if (!payload) {
      onNonSaveApply();
      return
    }

    sendRequest(
      apiUrl,
      uuids.update,
      requestParams,
      payload
    );
  }

  const onNonSaveApply = () => {
    // Once the payload is empty, no need to send to backend.
    setIsLoading(prev => {
      return {
        ...prev,
        updateWireless: false,
      }
    });

    getWirelessSSID();
    onBlockerApply();
  }

  const onDiscard = () => {
    onBlockerDiscard();
    setWirelessData(structuredClone(originalWirelessData));
  }

  useEffect(() => {
    if (!Object.hasOwn(dataMap ?? {}, uuids.update)) return;

    const updateResponse = getData(uuids.update);

    if (!updateResponse || updateResponse?.code === 400 || updateResponse?.code === 403 || updateResponse?.code === 500) {
      if (updateResponse?.code === 403 && updateResponse?.message === 'UnityPro subscription required') {
        setIsLinkModalOpen(true);
      } else {
        setAlertInfo({
          open: true,
          severity: "error",
          message: updateResponse?.message,
        });
      }
    } else {
      getWirelessSSID();
      onBlockerApply();

      setAlertInfo({
        open: true,
        severity: "success",
        message: (
          <Box>
            <Box
              sx={{
                fontSize: theme.typography.customFontSize[3],
                fontWeight: theme.typography.customFontWeight[3],
                pb: 1
              }}
            >
              Applied
            </Box>
            <Box>Changes applied successfully.</Box>
          </Box>
        )
      })

    }

    setIsLoading(prev => {
      return {
        ...prev,
        updateWireless: false,
      }
    });

  }, [dataMap?.[uuids.update]]);

  useEffect(() => {
    if(isDataChanged) {
      setGlobalOnApplyFn(onApply);
      setGlobalOnDiscardFn(onDiscard);
    }else{
      setGlobalOnApplyFn(null);
      setGlobalOnDiscardFn(null);
    }
  }, [isDataChanged])

  if (!scopeReady || !serviceStatus) return <Loading />;

  return (
    <>
      {
        (
          isLoading.wireless ||
          isLoading.radius ||
          isLoading.wirelessSchedule ||
          isLoading.macFilter ||
          isLoading.ipFilter ||
          isLoading.captivePortal ||
          isLoading.updateWireless
        ) &&
        <Loading />
      }

      <Box margin="20px">
        {showEditGeneral && (
          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 0 20px 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2
              }}
            >
              {
                isDataChanged &&
                <>
                  <SolidRectangleButton
                    width="150px"
                    height="32px"
                    onClick={() => onApply()}
                  >
                    Apply Changes
                  </SolidRectangleButton>
                  <OutlineRectangleButton
                    width="150px"
                    height="32px"
                    onClick={() => onDiscard()}
                  >
                    Discard Changes
                  </OutlineRectangleButton>
                </>
              }
            </Box>

            <SolidButton
              startIcon={<AddIcon />}
              width="250px"
              height="32px"
              onClick={handleOpenCreateModal}
              disabled={checkCreateBtnDisabled()}
            >
              Create New Wireless Network
            </SolidButton>
          </Box>
        )}

        <TableField
          {...table}
          drawerOpen={drawerOpen}
          handleRowOnClick={(row) => {
            setSelectedRowData(structuredClone(row));
            setDrawerOpen(true);
          }}
          getIsRowDisplayRemoveLine={row => row?.original?.isWaitingRemove}
        />

        {drawerOpen && (
          <DrawerField
            open={drawerOpen}
            handleClose={handleDrawerClose}
            width={750}
            contentKey={`Settings_Wireless_SSID_Drawer_${uuidv4()}`}
            commonContent="settingsWirelessSsid"
            contentProps={{
              isUnityPro,
              setAlertInfo,
              wirelessData,
              setWirelessData,
              onUpdateBlockerData,
              bandOptions: defaultBandOptions,
              selectedRowData,
              securityTypeAssembleList,
              securityTypeConfig,
              originalRowData,
              setOriginalRowData,
              clientsData,
              setClientsData,
              siteId,
              orgId,
              entId,
              hasManageOrg,
              getWirelessSSID,
              setDrawerOpen,
              isValidInputStyles,
              showEditGeneral,
              radiusProfileOptions,
              wirelessScheduleOptions,
              macFilterOptions,
              ipFilterOptions,
              captivePortalOptions,
              bandCount,
              setBandCount,
              onCreateNewProfile,
            }}
          />
        )}

      </Box>
      <CreateNewWirelessNetworkModal {
        ...{
          isUnityPro,
          setAlertInfo,
          wirelessData,
          setWirelessData,
          onUpdateBlockerData,
          bandOptions: defaultBandOptions,
          openCreateModal,
          setOpenCreateModal,
          securityTypeAssembleList,
          securityTypeConfig,
          siteId,
          orgId,
          entId,
          hasManageOrg,
          getWirelessSSID,
          setDrawerOpen,
          isValidInputStyles,
          radiusProfileOptions,
          wirelessScheduleOptions,
          macFilterOptions,
          ipFilterOptions,
          captivePortalOptions,
          bandCount,
          onCreateNewProfile,
        }
      }
      />

      <UnSavedConfirmModalContainer
        isOpen={isUnsavedBlocked}
        applyCallback={() => onApply()}
        discardCallback={() => onDiscard()}
      />

      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />

      {/* Create New Profile Modal */}
      <CreateNewProfileModal
        profileType={createProfileModal.profileType}
        openModal={createProfileModal.open}
        setOpenModal={onCloseCreateProfileModal}
        onClickLeaveNowBtn={() => {
          onCloseCreateProfileModal();
          setOpenCreateModal(false);
          setDrawerOpen(false);
          onGoToPage?.('profiles', createProfileModal.page);
          onBlockerDiscard(); // Discard unsaved changes and navigate to the new page.
        }}
      />

      <LinkModal
        transparentBackdrop
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? '/msp/license' : '/license');
        }}
      />
    </>
  );
};

export default WirelessSSID;
